<template>
  <div class="p-4">
    <!-- Missing billing info warning -->
    <div v-if="studentsWithMissingInfo.length > 0" class="mb-4 rounded-md bg-amber-50 p-4 border border-amber-200">
      <div class="flex">
        <div class="flex-shrink-0">
          <svg class="h-5 w-5 text-amber-400" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd" />
          </svg>
        </div>
        <div class="ml-3">
          <h3 class="text-sm font-medium text-amber-800">Missing billing information</h3>
          <div class="mt-2 text-sm text-amber-700">
            <p>The following students have incomplete billing information and cannot be invoiced accurately:</p>
            <ul class="list-disc list-inside mt-1">
              <li v-for="student in studentsWithMissingInfo" :key="student.name">
                <strong>{{ student.name }}</strong>: Missing {{ student.missing.join(', ') }}
              </li>
            </ul>
            <p class="mt-2 font-medium">Please update student records before generating invoices.</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Fee table -->
    <table class="min-w-full divide-y divide-gray-300">
      <thead>
        <tr>
          <th scope="col" class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900">Fee Type</th>
          <th scope="col" class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">Term 1</th>
          <th scope="col" class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">Term 2</th>
          <th scope="col" class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">Term 3</th>
          <th scope="col" class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">Term 4</th>
          <th scope="col" class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">Yearly Total</th>
        </tr>
      </thead>
      <tbody v-if="feeBreakdown" class="divide-y divide-gray-200">
        <!-- Row for each student -->
        <template v-for="studentBreakdown in feeBreakdown.studentsBreakdown" :key="studentBreakdown.studentId">
          <tr>
            <td class="py-2 text-sm text-right text-gray-600">Tuition Fee for {{ studentBreakdown.studentName }}</td>
            <td class="py-2 text-right text-sm text-gray-600">{{ formatCurrency(studentBreakdown.baseTuitionTerm) }}</td>
            <td class="py-2 text-right text-sm text-gray-600">{{ formatCurrency(studentBreakdown.baseTuitionTerm) }}</td>
            <td class="py-2 text-right text-sm text-gray-600">{{ formatCurrency(studentBreakdown.baseTuitionTerm) }}</td>
            <td class="py-2 text-right text-sm text-gray-600">{{ formatCurrency(studentBreakdown.baseTuitionTerm) }}</td>
            <td class="py-2 text-right text-sm font-medium text-gray-900">{{ formatCurrency(studentBreakdown.baseTuitionYearly) }}</td>
          </tr>
          <!-- SACE Levy row if applicable -->
          <tr v-if="studentBreakdown.saceLevy.type">
            <td class="py-2 pl-8 text-sm text-right text-gray-600">SACE {{ studentBreakdown.saceLevy.type }}</td>
            <td class="py-2 text-right text-sm text-gray-600">{{ formatCurrency(studentBreakdown.saceLevy.amountTerm1) }}</td>
            <td class="py-2 text-right text-sm text-gray-600">0</td>
            <td class="py-2 text-right text-sm text-gray-600">0</td>
            <td class="py-2 text-right text-sm text-gray-600">0</td>
            <td class="py-2 text-right text-sm font-medium text-gray-900">{{ formatCurrency(studentBreakdown.saceLevy.amountYearly) }}</td>
          </tr>
          <!-- Building Levy row - Removed -->
          <!-- Little Explorer Fee row if applicable -->
          <tr v-if="studentBreakdown.littleExplorer.applies">
            <td class="py-2 pl-8 text-sm text-right text-gray-600">Little Explorer Program</td>
            <td class="py-2 text-right text-sm text-gray-600">{{ formatCurrency(studentBreakdown.littleExplorer.amountTerm) }}</td>
            <td class="py-2 text-right text-sm text-gray-600">{{ formatCurrency(studentBreakdown.littleExplorer.amountTerm) }}</td>
            <td class="py-2 text-right text-sm text-gray-600">{{ formatCurrency(studentBreakdown.littleExplorer.amountTerm) }}</td>
            <td class="py-2 text-right text-sm text-gray-600">{{ formatCurrency(studentBreakdown.littleExplorer.amountTerm) }}</td>
            <td class="py-2 text-right text-sm font-medium text-gray-900">{{ formatCurrency(studentBreakdown.littleExplorer.amountYearly) }}</td>
          </tr>
          <!-- Discount Rows (Separate line for each discount type: Bursary, Staff, etc.) -->
          <template v-if="studentBreakdown.discountLineItems && studentBreakdown.discountLineItems.length > 0">
            <tr v-for="(discountItem, index) in studentBreakdown.discountLineItems" :key="'discount-' + index">
              <td class="py-2 pl-8 text-sm text-right text-gray-600">{{ discountItem.name }}</td>
              <td class="py-2 text-right text-sm text-red-600">{{ formatCurrency(discountItem.amountTerm1) }}</td>
              <td class="py-2 text-right text-sm text-red-600">{{ formatCurrency(discountItem.amountTerm2) }}</td>
              <td class="py-2 text-right text-sm text-red-600">{{ formatCurrency(discountItem.amountTerm3) }}</td>
              <td class="py-2 text-right text-sm text-red-600">{{ formatCurrency(discountItem.amountTerm4) }}</td>
              <td class="py-2 text-right text-sm font-medium text-red-600">{{ formatCurrency(discountItem.amountYearly) }}</td>
            </tr>
          </template>
        </template>
        <!-- Family Subtotal section -->
         <tr class="border-t border-gray-300">
          <td class="py-2 text-right text-sm font-semibold text-gray-700">Family Subtotal</td>
          <td class="py-2 text-right text-sm font-semibold text-gray-700">{{ formatCurrency(feeBreakdown.familySubtotals.term1) }}</td>
          <td class="py-2 text-right text-sm font-semibold text-gray-700">{{ formatCurrency(feeBreakdown.familySubtotals.term2) }}</td>
          <td class="py-2 text-right text-sm font-semibold text-gray-700">{{ formatCurrency(feeBreakdown.familySubtotals.term3) }}</td>
          <td class="py-2 text-right text-sm font-semibold text-gray-700">{{ formatCurrency(feeBreakdown.familySubtotals.term4) }}</td>
          <td class="py-2 text-right text-sm font-semibold text-gray-700">{{ formatCurrency(feeBreakdown.familySubtotals.yearly) }}</td>
        </tr>
        <!-- Family Size Discount row -->
        <tr v-if="feeBreakdown.familySizeDiscount.applies && feeBreakdown.familySizeDiscount.rate > 0">
          <td class="py-2 text-right text-sm font-bold text-gray-600">Family Size Discount ({{ Math.round(feeBreakdown.familySizeDiscount.rate * 100) }}%)</td>
          <td class="py-2 text-right text-sm font-bold text-red-600">{{ formatCurrency(-feeBreakdown.familySizeDiscount.amountTerm1) }}</td>
          <td class="py-2 text-right text-sm font-bold text-red-600">{{ formatCurrency(-feeBreakdown.familySizeDiscount.amountTerm2) }}</td>
          <td class="py-2 text-right text-sm font-bold text-red-600">{{ formatCurrency(-feeBreakdown.familySizeDiscount.amountTerm3) }}</td>
          <td class="py-2 text-right text-sm font-bold text-red-600">{{ formatCurrency(-feeBreakdown.familySizeDiscount.amountTerm4) }}</td>
          <td class="py-2 text-right text-sm font-bold text-red-600">{{ formatCurrency(-feeBreakdown.familySizeDiscount.amountYearly) }}</td>
        </tr>
        <!-- Final Total row -->
        <tr class="border-t-2 border-gray-400">
          <td class="py-2 text-right text-sm font-bold text-gray-900">Family Total</td>
          <td class="py-2 text-right text-sm font-bold text-gray-900">{{ formatCurrency(feeBreakdown.finalTotals.term1) }}</td>
          <td class="py-2 text-right text-sm font-bold text-gray-900">{{ formatCurrency(feeBreakdown.finalTotals.term2) }}</td>
          <td class="py-2 text-right text-sm font-bold text-gray-900">{{ formatCurrency(feeBreakdown.finalTotals.term3) }}</td>
          <td class="py-2 text-right text-sm font-bold text-gray-900">{{ formatCurrency(feeBreakdown.finalTotals.term4) }}</td>
          <td class="py-2 text-right text-sm font-bold text-gray-900">{{ formatCurrency(feeBreakdown.finalTotals.yearly) }}</td>
        </tr>
        <!-- Invoice Generation row -->
        <tr>
          <td class="py-2 text-right text-sm font-semibold text-gray-900">Invoice Generation</td>
          <td v-for="term in [1, 2, 3, 4]" :key="term" class="py-2 text-right">
            <XeroInvoiceGenerator
              :family-code="familyCode"
              :term="term"
              :students="students"
              :term-total="feeBreakdown.finalTotals[`term${term}`]"
              :invoice_id="students.find(s => s.Invoices && s.Invoices[`Term${term}${new Date().getFullYear()}Invoice`])?.Invoices[`Term${term}${new Date().getFullYear()}Invoice`]"
              :is-edit-mode="isEditMode"
              @invoice-generated="handleInvoiceGenerated"
              @invoice-voided="handleInvoiceVoided"
              class="text-xs inline-block"
            />
          </td>
          <td class="py-2 text-right">
            <button
              @click="isEditMode = !isEditMode"
              class="text-xs font-semibold rounded-md px-2 py-1 ring-1 ring-inset"
              :class="{
                'text-indigo-600 hover:text-indigo-500 ring-indigo-600/20 hover:bg-indigo-50': !isEditMode,
                'text-red-600 hover:text-red-500 ring-red-600/20 hover:bg-red-50': isEditMode
              }">
              {{ isEditMode ? 'Done' : 'Edit' }}
            </button>
          </td>
        </tr>
        <!-- Check Xero Invoices Row -->
        <tr>
          <td class="py-2 text-right text-sm">
            <button 
              @click="checkXeroInvoices" 
              :disabled="isCheckingInvoices" 
              class="inline-flex items-center text-xs font-semibold rounded-md px-2 py-1 ring-1 ring-inset text-gray-600 hover:text-gray-800 ring-gray-600/20 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-wait"
            >
              <svg v-if="isCheckingInvoices" class="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {{ isCheckingInvoices ? 'Checking...' : 'Check Xero Invoices' }}
            </button>
          </td>
          <td v-for="term in [1, 2, 3, 4]" :key="term" class="py-2 text-right text-sm">
            <div v-if="xeroInvoiceTotals[term]">
              <div v-if="xeroInvoiceTotals[term].error" class="text-red-500 text-xs" title="Could not fetch details.">
                Error
              </div>
              <div v-else>
                <div class="font-bold" :class="{ 'text-green-600': xeroInvoiceTotals[term].amountDue === 0, 'text-red-600': xeroInvoiceTotals[term].amountDue > 0 }">
                   {{ formatCurrency(xeroInvoiceTotals[term].total) }}
                </div>
                <div class="text-xs text-gray-500">
                  Due: {{ formatCurrency(xeroInvoiceTotals[term].amountDue) }}
                </div>
              </div>
            </div>
            <div v-else-if="isCheckingInvoices && getInvoiceIdForTerm(term)" class="text-xs text-gray-400">
              ...
            </div>
          </td>
          <td></td>
        </tr>
      </tbody>
       <tbody v-else>
        <tr>
          <td colspan="6" class="text-center py-4 text-gray-500">Calculating fees...</td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script setup>
import { defineProps, defineEmits, ref, computed } from 'vue';
import XeroInvoiceGenerator from './XeroInvoiceGenerator.vue';
import { formatCurrency, getMissingBillingInfo } from '../utils/feeUtils';
import axios from 'axios';

// eslint-disable-next-line no-unused-vars
const props = defineProps({
  students: {
    type: Array,
    required: true
  },
  familyCode: {
    type: String,
    required: true
  },
  invoices: {
    type: Object,
    default: () => ({})
  },
  generatingInvoice: {
    type: Boolean,
    default: false
  },
  feeBreakdown: {
    type: Object,
    default: null
  }
});

const emit = defineEmits(['invoice-generated', 'invoice-voided']);

// Computed property to find students with missing billing info
const studentsWithMissingInfo = computed(() => {
  if (!props.students || props.students.length === 0) return [];

  return props.students
    .map(student => {
      const missing = getMissingBillingInfo(student);
      if (missing.length > 0) {
        return {
          name: `${student.firstNameLegal} ${student.lastNameLegal}`,
          missing: missing
        };
      }
      return null;
    })
    .filter(item => item !== null);
});

const handleInvoiceGenerated = (data) => {
  emit('invoice-generated', data);
};

const handleInvoiceVoided = (data) => {
  emit('invoice-voided', data);
};

const isEditMode = ref(false);

const isCheckingInvoices = ref(false);
const xeroInvoiceTotals = ref({});

const getInvoiceIdForTerm = (term) => {
  const year = new Date().getFullYear();
  const invoiceField = `Term${term}${year}Invoice`;
  const studentWithInvoice = props.students.find(s => s.Invoices && s.Invoices[invoiceField]);
  return studentWithInvoice ? studentWithInvoice.Invoices[invoiceField] : null;
};

const checkXeroInvoices = async () => {
    isCheckingInvoices.value = true;
    xeroInvoiceTotals.value = {};
    const promises = [];

    for (const term of [1, 2, 3, 4]) {
        const invoiceId = getInvoiceIdForTerm(term);
        if (invoiceId) {
            const promise = axios.get(`/api/getInvoice?invoiceId=${invoiceId}`)
                .then(response => {
                    if (response.data) {
                        xeroInvoiceTotals.value[term] = response.data;
                    }
                })
                .catch(error => {
                    console.error(`Error fetching invoice for term ${term} (ID: ${invoiceId}):`, error);
                    xeroInvoiceTotals.value[term] = { error: true };
                });
            promises.push(promise);
        }
    }

    await Promise.all(promises);
    isCheckingInvoices.value = false;
};
</script> 